import { SwArgsApiStore } from "@sourcewhale/shared/src/svelte/api-stores/sw-api-store";
import { derived, writable } from "svelte/store";
import { getCompanyInsights } from "../../../api/extension";
import {
  formatEmployees,
  formatJobs,
  formatLocation,
  formatNews,
  formatText,
} from "./utils";

// Options for the company insights store
// Takes an object with:
// - isTabOpen: boolean - whether the company insights tab is open
// - obj: {candidateId, company} - the candidate and company to get insights for
// - origin: "todos" | "search" - where the request is coming from
// - isOverlay: boolean - whether the insights are shown in an overlay (true) or modal (false)
export const companyInsightsOptsStore = writable({});

const CompanyInsightsStore = () => {
  const store = SwArgsApiStore({
    get: getCompanyInsights,
    init: () => ({}),
    dataTransform: (x) => {
      // Response is {candidateId: companyInsightsObject}
      // so we want to extract the first (and only) dict value
      const ret = Object.values(x)[0];

      if (ret) {
        formatText(ret);
        formatLocation(ret);
        formatEmployees(ret);
        formatNews(ret);
        formatJobs(ret);
      }

      return ret;
    },
  });

  // Keep track of items that change the results so we know when to make a new request
  const current = {
    candidateId: null,
    company: null,
    companyId: null,
    webCandidateId: null,
    webCompanyId: null,
  };

  // Make a derived store to which we can subscribe to set the SwArgsApiStore above
  const shouldMakeRequest = derived(
    [companyInsightsOptsStore],
    ([$companyInsightsOpts]) => {
      const { isTabOpen, obj, origin } = $companyInsightsOpts;

      // Don't make any requests if the company insights tab is unopened
      // or if there is no todo / candidateId / company / companyId currently set.
      if (
        !isTabOpen ||
        !obj ||
        !(obj.candidateId || obj.company || obj.companyId)
      )
        return;

      if (origin === "todos") {
        // Request is made when the argsStore of the store is changed to a new value
        const { candidateId, company } = obj;
        if (candidateId && candidateId !== current.candidateId) {
          current.candidateId = candidateId;
          current.company = company;
          store.set([{ candidate: candidateId, origin }]);
          return;
        }

        // If the company is updated, we want to make a new request without changing the argsStore,
        // so we use the store's get() method.
        if (company && company !== current.company) {
          current.company = company;
          store.get();
        }
      } else if (["company", "project"].includes(origin)) {
        const { companyId } = obj;
        if (companyId && companyId !== current.companyId) {
          current.companyId = companyId;
          store.set([{ companyId, origin }]);
          return;
        }
      }
      // Search page needs to send company name only
      else if (origin === "search") {
        const { company, webCandidateId, webCompanyId } = obj;

        current.company = company;
        current.webCandidateId = webCandidateId;
        current.webCompanyId = webCompanyId;

        store.set([
          { companyName: company, webCandidateId, webCompanyId, origin },
        ]);
      }
    },
  );

  shouldMakeRequest.subscribe(() => {});
  return store;
};

export const companyInsightsStore = CompanyInsightsStore();

export const companyInsightsTabs = [
  {
    label: "About",
    slot: "tab0",
    onclick: () => {},
  },
  {
    label: "News",
    slot: "tab1",
    onclick: () => {},
  },
  {
    label: "Jobs",
    slot: "tab2",
    onclick: () => {},
  },
  {
    label: "Funding",
    slot: "tab3",
    onclick: () => {},
  },
];

// Extract and store tags from the news and jobs sections
const TagsStores = (key) =>
  // Initialize the tags store when companyInsightsStore changes
  derived([companyInsightsStore], ([$store]) => {
    // Don't do anything if the companyInsightsStore is empty
    if (!$store) return [];

    // Extract all unique tags from the news or jobs sections (key)
    // List of all the tags in the format MultiSelectDropdown expects
    const tags = ($store[key] || []).flatMap((x) => x.tags || []);
    return [...new Set(tags)].map((tag) => ({ id: tag, label: tag }));
  });

export const newsTagsStore = TagsStores("news");
export const jobTagsStore = TagsStores("jobs");

export const isOverlayStore = derived(
  [companyInsightsOptsStore],
  ([$companyInsightsOpts]) => $companyInsightsOpts?.isOverlay || false,
);
